import React, {useEffect, useState} from 'react';
import {Cell, Pie, PieChart, ResponsiveContainer, Tooltip} from 'recharts';
import figureConverter from '../../../../tools/figureConverter';
import api from '../../../../api/axios';
import {useDatePickerStore} from '../DatepickerStore'; // Importiere den Datepicker Store
import Cookies from 'js-cookie';
import {format} from 'date-fns';

// Farben für die Punkte (initiale Standardfarben)
const COLORS = ['#8884d8', '#82ca9d', '#ffc658'];

const LocationReportChart = () => {
    const [colors, setColors] = useState(COLORS);
    const [data, setData] = useState([]); // Zustand für die Standortdaten
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const startDate = useDatePickerStore((state) => state.startDate); // Verwende den Datepicker Store
    const endDate = useDatePickerStore((state) => state.endDate);

    useEffect(() => {
        const getCSSBackgroundColor = (className) => {
            const element = document.createElement('div');
            element.className = className;
            document.body.appendChild(element);
            const bgColor = window.getComputedStyle(element).backgroundColor;
            document.body.removeChild(element);
            return bgColor;
        };

        const color1 = getCSSBackgroundColor('bg-color-1');
        const color2 = getCSSBackgroundColor('bg-color-4');
        const color3 = getCSSBackgroundColor('bg-color-6');
        setColors([color1, color2, color3]);
    }, []);

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            setError(null);
            try {
                // Datum formatieren
                const formattedStartDate = format(startDate, 'yyyy-MM-dd');
                const formattedEndDate = format(endDate, 'yyyy-MM-dd');

                // Access Token aus Cookies abrufen
                const accessToken = Cookies.get('accessToken');
                if (!accessToken) {
                    throw new Error('Access Token fehlt. Bitte melden Sie sich erneut an.');
                }

                // Anfrage mit Authorization-Header durchführen
                const response = await api.get(
                    `/api/v1/companysite?start=${formattedStartDate}&end=${formattedEndDate}`,
                    {
                        headers: {
                            Authorization: `Bearer ${accessToken}`,
                        },
                    }
                );

                const {companySites} = response.data;

                // Summiere die Werte für jeden Standort
                const formattedData = Object.entries(companySites).map(([siteName, periods]) => {
                    let totalRevenue = 0;
                    let totalCosts = 0;
                    let totalYield = 0;

                    Object.values(periods).forEach((period) => {
                        totalRevenue += parseFloat(period.revenue);
                        totalCosts += parseFloat(period.costs);
                        totalYield += parseFloat(period.yield);
                    });

                    return {
                        name: siteName,
                        value: totalRevenue, // Summierte revenue als Wert für das Diagramm
                        revenue: totalRevenue,
                        costs: totalCosts,
                        yield: totalYield,
                    };
                });

                setData(formattedData);
            } catch (err) {
                console.error('Fehler beim Abrufen der Daten:', err);
                setError(err.message || 'Unbekannter Fehler');
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [startDate, endDate]);

    const CustomTooltip = ({active, payload}) => {
        if (active && payload && payload.length) {
            const {name, revenue, costs, yield: yieldValue} = payload[0].payload;
            return (
                <div className="custom-tooltip bg-color-3 rounded-lg p-2 opacity-90 border border-color-1">
                    <p>{`Standort: ${name}`}</p>
                    <p>{`Umsatz: ${figureConverter(revenue, false)}`}</p>
                    <p>{`Kosten: ${figureConverter(costs, false)}`}</p>
                    <p>{`Rohertrag 1: ${figureConverter(yieldValue, false)}`}</p>
                </div>
            );
        }
        return null;
    };

    return (
        <div className='w-full h-full flex items-center justify-center pt-6 pb-2'>
            {loading && <p>Laden...</p>}
            {error && <p>Fehler: {error}</p>}
            {!loading && !error && (
                <ResponsiveContainer width="100%" height={150}>
                    <PieChart>
                        <Pie
                            data={data}
                            dataKey="value"
                            nameKey="name"
                            cx="50%"
                            cy="50%"
                            outerRadius={70}
                            innerRadius={45}
                            fill="#8884d8"
                        >
                            {data.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={colors[index % colors.length]}/>
                            ))}
                        </Pie>
                        <Tooltip content={<CustomTooltip/>}/>
                    </PieChart>
                </ResponsiveContainer>
            )}
        </div>
    );
};

export default LocationReportChart;
